// Members Page — the CRM-style master list with full profile data.
//
// Unlike the member directory (which hides addresses and private contact
// fields), this view surfaces ALL collected onboarding data: a summary table
// with an expandable per-member detail panel, search/filters, and a CSV
// export for communication use. Admins see the whole org; facilitators see
// the same full-detail view but server-scoped to members of forums they
// facilitate.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI, forumsAPI, paymentsAPI, ADMIN_MEMBER_EXPORT_FIELDS } from '../api/client';
import { confirmResendInvite, inviteSentLabel } from '../lib/inviteSent';
import { useAuth } from '../context/useAuth';
import { isAdminRole, isFacilitatorRole, roleLabel } from '../utils/roles';
import InviteMemberModal from '../components/InviteMemberModal';
import { ManageMemberPanel } from '../components/ManageMemberPanel';
import BillingDayCard from '../components/BillingStartDateCard';
import HorizontalScroll from '../components/HorizontalScroll';
import {
  CoverageTimeline,
  earliestUpcomingCharge,
  formatChargeDate,
} from '../components/MemberBillingSection';
import toast from 'react-hot-toast';
import {
  Users,
  Search,
  Download,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Loader2,
  X,
  ExternalLink,
  SlidersHorizontal,
  UserPlus,
  Send,
  Award,
  ClipboardList,
} from 'lucide-react';
import type {
  AdminMemberRow,
  BillingOverviewRow,
  ForumListItem,
  PaymentStatus,
} from '../types';

type SortKey = 'name' | 'forum' | 'role' | 'location' | 'payment' | 'joined';

const ROLE_FILTER_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'guest', label: 'Guest' },
  { value: 'facilitator', label: 'Facilitator' },
  { value: 'admin', label: 'Admin' },
];

function MultiSelectDropdown<T extends string | number>({
  emptyLabel,
  options,
  selected,
  onChange,
  clearLabel = 'Clear',
}: {
  emptyLabel: string;
  options: { value: T; label: string }[];
  selected: T[];
  onChange: (next: T[]) => void;
  clearLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const selectedLabels = options
    .filter((option) => selected.includes(option.value))
    .map((option) => option.label);
  const buttonLabel = selectedLabels.length === 0
    ? emptyLabel
    : selectedLabels.length === 1
      ? selectedLabels[0]
      : `${selectedLabels[0]} +${selectedLabels.length - 1}`;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`flex items-center gap-2 min-w-[12rem] px-3 py-2 border rounded-lg bg-white text-sm text-left ${
          selected.length > 0 ? 'border-navy text-navy' : 'border-gray-300 text-gray-700'
        }`}
      >
        <span className="flex-1 truncate">{buttonLabel}</span>
        {selected.length > 0 && (
          <span className="text-xs font-medium bg-navy text-white px-1.5 py-0.5 rounded-full">
            {selected.length}
          </span>
        )}
        <ChevronDown size={16} className={`flex-shrink-0 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute z-20 mt-1 w-72 max-h-72 flex flex-col bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="overflow-y-auto overscroll-contain [scrollbar-gutter:stable]">
            {options.map((option) => (
              <label
                key={String(option.value)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-off-white cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={() =>
                    onChange(
                      selected.includes(option.value)
                        ? selected.filter((value) => value !== option.value)
                        : [...selected, option.value],
                    )
                  }
                  className="rounded border-gray-300 text-navy focus:ring-gold"
                />
                <span className="truncate">{option.label}</span>
              </label>
            ))}
          </div>
          <button
            type="button"
            disabled={selected.length === 0}
            onClick={() => onChange([])}
            className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-off-white border-t border-gray-100 disabled:opacity-40 disabled:hover:bg-white"
          >
            {clearLabel}
          </button>
        </div>
      )}
    </div>
  );
}

const PAYMENT_BADGES: Record<PaymentStatus, string> = {
  active: 'bg-green-100 text-green-700',
  trialing: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  past_due: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-100 text-gray-600',
  unpaid: 'bg-gray-100 text-gray-600',
};

// Human labels for the coded onboarding choices (mirrors the backend enums).
const REVENUE_LABELS: Record<string, string> = {
  under_500k: '<$500k',
  '500k_1m': '$500k–$1M',
  '1m_5m': '$1M–$5M',
  '5m_10m': '$5M–$10M',
  '10m_25m': '$10M–$25M',
  '25m_plus': '$25M+',
};

const GROWTH_LABELS: Record<string, string> = {
  flat_declining: 'Flat/Declining',
  '1_10': '1–10%',
  '10_25': '10–25%',
  '25_plus': '25%+',
};

const MEMBERSHIP_LABELS: Record<string, string> = {
  monthly: 'Monthly – $400',
  annual: 'Annual – $4,800',
  stakeholder: 'Stakeholder – $500/mo',
  scholarship: 'Scholarship',
};

function paymentLabel(status: PaymentStatus): string {
  // There is no free trial: Stripe reports "trialing" for a member whose
  // card is on file with the first/next charge deferred to the org billing
  // day. (A member who already paid this month is reported "active" by the
  // backend, so "signed up" here always means not-yet-charged.)
  if (status === 'trialing') return 'signed up';
  return status.replace('_', ' ');
}

const STAFF_ROLES = new Set(['facilitator', 'admin', 'god_mode']);

function isZeroDues(amount: string | null | undefined): boolean {
  if (amount == null || amount === '') return false;
  const n = Number(amount);
  return Number.isFinite(n) && n === 0;
}

function nextChargeCaption(
  row: {
    role: string;
    monthly_amount: string | null;
    is_paused: boolean;
    has_subscription: boolean;
    payment_status: string;
    cancel_at_period_end?: boolean;
  },
  nextCharge: { date: Date } | null,
): { text: string; className: string } {
  if (row.is_paused) return { text: 'Paused', className: 'text-yellow-700' };
  if (STAFF_ROLES.has(row.role)) return { text: 'Staff · not billed', className: 'text-gray-500' };
  if (isZeroDues(row.monthly_amount)) return { text: '$0 · no charge', className: 'text-gray-500' };
  if (nextCharge) {
    const date = nextCharge.date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    if (row.cancel_at_period_end && row.payment_status !== 'canceled') {
      return { text: `Ends ${date}`, className: 'text-slate-700' };
    }
    if (row.payment_status === 'trialing') {
      return { text: `First charge ${date}`, className: 'text-gray-700' };
    }
    return { text: date, className: 'text-gray-700' };
  }
  if (!row.has_subscription) {
    return { text: 'Checkout not finished', className: 'text-amber-700' };
  }
  return { text: '—', className: 'text-gray-400' };
}

function capitalize(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function joinParts(...parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join(', ');
}

function sortValue(row: AdminMemberRow, key: SortKey): string | number {
  switch (key) {
    case 'name':
      return row.full_name.toLowerCase();
    case 'forum':
      return (row.forums[0]?.name ?? '').toLowerCase();
    case 'role':
      return row.role;
    case 'location':
      return `${row.home_state || row.location_state} ${row.home_city || row.location_city}`.toLowerCase();
    case 'payment':
      return row.payment_status;
    case 'joined':
      return row.date_joined;
  }
}

interface DetailField {
  label: string;
  value: string;
  // Long-form text is rendered full-width below the compact fields.
  long?: boolean;
}

interface DetailGroup {
  title: string;
  fields: DetailField[];
}

// Everything collected at onboarding, organized for the expanded detail panel.
function detailGroups(m: AdminMemberRow): DetailGroup[] {
  const formatDateTime = (value: string | null) =>
    value
      ? new Date(value).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '';
  return [
    {
      title: 'Contact & Address',
      fields: [
        { label: 'Email', value: m.email },
        { label: 'Cell phone', value: m.cell_phone },
        { label: 'Gender', value: capitalize(m.gender) },
        { label: 'Home address', value: m.home_address },
        {
          label: 'Home city / state / zip',
          value: joinParts(m.home_city, m.home_state, m.home_zip),
        },
        { label: 'Home country', value: m.home_country },
      ],
    },
    {
      title: 'Business',
      fields: [
        { label: 'Company', value: m.business_type },
        { label: 'Job title', value: m.title },
        { label: 'Position', value: capitalize(m.position) },
        { label: 'Industry', value: m.industry },
        { label: 'Website', value: m.website },
        {
          label: 'Years in business',
          value: m.years_in_business != null ? String(m.years_in_business) : '',
        },
        { label: 'Business age', value: capitalize(m.business_age) },
        { label: 'Company structure', value: (m.company_structure ?? []).join(', ') },
        { label: 'Revenue range', value: REVENUE_LABELS[m.revenue_range] ?? m.revenue_range },
        { label: 'Growth rate', value: GROWTH_LABELS[m.growth_rate] ?? m.growth_rate },
        { label: 'Staff count', value: m.num_staff != null ? String(m.num_staff) : '' },
        { label: 'Business address', value: m.business_address },
        {
          label: 'Business city / state / zip',
          value: joinParts(m.business_city, m.business_state, m.business_zip),
        },
        { label: 'Business country', value: m.business_country },
      ],
    },
    {
      title: 'Family',
      fields: [
        { label: 'Family status', value: capitalize(m.family_status) },
        { label: 'Has children', value: m.has_children ? 'Yes' : 'No' },
        {
          label: 'Number of children',
          value: m.num_children != null ? String(m.num_children) : '',
        },
        { label: 'Spouse name', value: m.spouse_name },
        { label: 'Spouse email', value: m.spouse_email },
      ],
    },
    {
      title: 'Faith & Purpose',
      fields: [
        { label: 'Church attending', value: m.church_attending },
        { label: 'Areas of expertise', value: (m.areas_of_expertise ?? []).join(', ') },
        { label: 'Growth areas', value: (m.growth_areas ?? []).join(', ') },
        { label: 'Faith journey', value: m.faith_journey, long: true },
        { label: 'Why Iron Forums', value: m.why_iron_forums, long: true },
        { label: 'Purpose statement', value: m.purpose_statement, long: true },
        { label: 'Season goal', value: m.quarterly_goal, long: true },
        { label: 'Bio', value: m.bio, long: true },
        { label: 'Hobbies & interests', value: m.hobbies_interests, long: true },
      ],
    },
    {
      title: 'Membership & Payment',
      fields: [
        { label: 'Role', value: roleLabel(m.role) },
        { label: 'Snapshot type', value: capitalize(m.snapshot_type) },
        { label: 'Forums', value: m.forums.map((f) => f.name).join(', ') },
        {
          label: 'Membership level',
          value: MEMBERSHIP_LABELS[m.membership_level] ?? m.membership_level,
        },
        { label: 'Payment status', value: capitalize(paymentLabel(m.payment_status)) },
        { label: 'Monthly price', value: m.monthly_price != null ? `$${m.monthly_price}` : '' },
        { label: 'Profile completed', value: m.profile_completed ? 'Yes' : 'No' },
        { label: 'Onboarding seen', value: m.has_seen_onboarding ? 'Yes' : 'No' },
        { label: 'Date joined', value: formatDateTime(m.date_joined) },
        { label: 'Last login', value: formatDateTime(m.last_login) },
      ],
    },
    {
      title: 'Admin-Requested Info',
      fields: (m.requirement_answers ?? []).map((a) => ({
        label: a.question,
        value: a.answer,
        long: true,
      })),
    },
  ];
}

function MemberDetailPanel({ member }: { member: AdminMemberRow }) {
  return (
    <div className="px-6 py-5 bg-off-white/60 space-y-5">
      {detailGroups(member).map((group) => {
        const shortFields = group.fields.filter((f) => !f.long);
        const longFields = group.fields.filter((f) => f.long && f.value);
        if (shortFields.length === 0 && longFields.length === 0) return null;
        return (
          <div key={group.title}>
            <h4 className="text-xs font-semibold text-navy uppercase tracking-wide mb-2">
              {group.title}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
              {shortFields.map((field) => (
                <div key={field.label} className="text-sm">
                  <span className="text-gray-500">{field.label}: </span>
                  {field.value ? (
                    <span className="text-gray-900">{field.value}</span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </div>
              ))}
            </div>
            {longFields.map((field) => (
              <div key={field.label} className="text-sm mt-2">
                <span className="text-gray-500">{field.label}: </span>
                <span className="text-gray-900 whitespace-pre-wrap">{field.value}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default function AdminMembers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const isFacilitator = isFacilitatorRole(user?.role);
  const [members, setMembers] = useState<AdminMemberRow[]>([]);
  const [forums, setForums] = useState<ForumListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    () => new Set(ADMIN_MEMBER_EXPORT_FIELDS.map((f) => f.key)),
  );
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [forumFilters, setForumFilters] = useState<number[]>([]);
  const [roleFilters, setRoleFilters] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [managingId, setManagingId] = useState<number | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [view, setView] = useState<'members' | 'billing'>('members');
  const [billingRows, setBillingRows] = useState<BillingOverviewRow[] | null>(null);
  const [facilitatedForumIds, setFacilitatedForumIds] = useState<Set<number>>(
    () => new Set(),
  );
  const debounceRef = useRef<number | undefined>(undefined);
  const hasLoadedMembers = useRef(false);

  const managingMember = useMemo(
    () => members.find((m) => m.id === managingId) ?? null,
    [members, managingId],
  );

  // Admins can filter across every forum; facilitators only across their own
  // (the backend scopes their rows regardless of what's requested).
  useEffect(() => {
    const load = isAdmin ? forumsAPI.getAll() : forumsAPI.getMyForums();
    load.then(setForums).catch(err => {
      console.error('Error loading forums:', err);
    });
    forumsAPI.getFacilitating()
      .then((list) => setFacilitatedForumIds(new Set(list.map((forum) => forum.id))))
      .catch(() => setFacilitatedForumIds(new Set()));
  }, [isAdmin]);

  // Debounce the search box into the server-side query.
  useEffect(() => {
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(debounceRef.current);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    if (!hasLoadedMembers.current) setIsLoading(true);
    usersAPI
      .getAdminMembers({
        search: search || undefined,
        forum: forumFilters.length ? forumFilters.join(',') : undefined,
        role: roleFilters.length ? roleFilters.join(',') : undefined,
        include_inactive: showInactive ? '1' : undefined,
        inactive_only: showInactive ? '1' : undefined,
        pending: pendingOnly || undefined,
      })
      .then(data => {
        if (!cancelled) setMembers(data);
      })
      .catch(err => {
        console.error('Error loading members:', err);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
          hasLoadedMembers.current = true;
        }
      });
    return () => {
      cancelled = true;
    };
  }, [search, forumFilters, roleFilters, showInactive, pendingOnly, reloadKey]);

  const sortedMembers = useMemo(() => {
    const rows = [...members];
    rows.sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av < bv) return sortAsc ? -1 : 1;
      if (av > bv) return sortAsc ? 1 : -1;
      return 0;
    });
    return rows;
  }, [members, sortKey, sortAsc]);

  // The Billing view: one org-wide fetch, then joined against the filtered
  // member list so the search box and filters work the same in both views
  // (and Manage always resolves to a loaded member row).
  useEffect(() => {
    if ((!isAdmin && !isFacilitator) || view !== 'billing') return;
    let cancelled = false;
    paymentsAPI
      .getBillingOverview()
      .then((data) => {
        if (!cancelled) setBillingRows(data.members);
      })
      .catch((err) => console.error('Error loading billing overview:', err));
    return () => {
      cancelled = true;
    };
  }, [isAdmin, isFacilitator, view, reloadKey]);

  const visibleBillingRows = useMemo(() => {
    if (!billingRows) return null;
    const byId = new Map(billingRows.map((row) => [row.id, row]));
    return sortedMembers
      .map((member) => byId.get(member.id))
      .filter((row): row is BillingOverviewRow => row !== undefined);
  }, [billingRows, sortedMembers]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const toggleField = (key: string) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await usersAPI.exportAdminMembersCsv({
        search: search || undefined,
        forum: forumFilters.length ? forumFilters.join(',') : undefined,
        role: roleFilters.length ? roleFilters.join(',') : undefined,
        fields: Array.from(selectedFields).join(','),
        include_inactive: showInactive ? '1' : undefined,
        inactive_only: showInactive ? '1' : undefined,
        pending: pendingOnly || undefined,
      });
      setShowFieldPicker(false);
    } catch (err) {
      console.error('Error exporting members CSV:', err);
      window.alert('Could not export the member list. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const headers: { key: SortKey; label: string; align?: string }[] = [
    { key: 'name', label: 'Member' },
    { key: 'forum', label: 'Forum' },
    { key: 'role', label: 'Role' },
    { key: 'location', label: 'City / State' },
    { key: 'payment', label: 'Payment' },
    { key: 'joined', label: 'Joined' },
  ];

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const showForumFilter = isAdmin || forums.length > 1;

  return (
    <div className="space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center">
          <Users className="text-gold" size={28} />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-navy">
            {isAdmin ? 'All Members' : 'Members'}
          </h1>
          <p className="text-gray-600">
            {isAdmin
              ? 'Org-wide member list with full profile data'
              : 'Members of the Forums you facilitate, with full profile data'}{' '}
            ({members.length} {members.length === 1 ? 'member' : 'members'})
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-light transition"
        >
          <UserPlus size={16} />
          Invite
        </button>
        <button
          onClick={() => setShowFieldPicker(true)}
          disabled={isExporting}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-light transition disabled:opacity-60"
        >
          {isExporting ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Download size={16} />
          )}
          Export CSV
        </button>
      </div>

      {(isAdmin || isFacilitator) && (
        <div className="flex gap-2">
          {(
            [
              { key: 'members', label: 'Members' },
              { key: 'billing', label: 'Billing' },
            ] as const
          ).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                view === key
                  ? 'bg-navy text-white'
                  : 'bg-white text-gray-600 hover:text-navy shadow-sm'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {isAdmin && view === 'billing' && <BillingDayCard />}

      {showFieldPicker && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-semibold text-navy">Choose columns to export</h3>
              <button onClick={() => setShowFieldPicker(false)} aria-label="Close">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Pick which fields to include in the CSV. All are selected by default.
            </p>
            <div className="flex gap-3 mb-3 text-sm">
              <button
                onClick={() => setSelectedFields(new Set(ADMIN_MEMBER_EXPORT_FIELDS.map((f) => f.key)))}
                className="text-navy font-medium hover:underline"
              >
                Select all
              </button>
              <span className="text-gray-300">·</span>
              <button
                onClick={() => setSelectedFields(new Set())}
                className="text-navy font-medium hover:underline"
              >
                Select none
              </button>
            </div>
            <div className="flex-1 overflow-y-auto border border-gray-100 rounded-lg p-3 grid grid-cols-2 gap-2">
              {ADMIN_MEMBER_EXPORT_FIELDS.map((f) => (
                <label
                  key={f.key}
                  className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedFields.has(f.key)}
                    onChange={() => toggleField(f.key)}
                    className="rounded border-gray-300 text-navy focus:ring-gold"
                  />
                  {f.label}
                </label>
              ))}
            </div>
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setShowFieldPicker(false)}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting || selectedFields.size === 0}
                className="flex-1 bg-navy text-white py-2.5 rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download size={16} />}
                {isExporting ? 'Exporting...' : 'Download CSV'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search + filters */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-3">
        <div className="relative flex-1 max-w-md md:min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by name, email, or company..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
          />
        </div>
        {showForumFilter && (
          <MultiSelectDropdown
            emptyLabel={isAdmin ? 'All Forums' : 'All my Forums'}
            options={forums.map((forum) => ({ value: forum.id, label: forum.name }))}
            selected={forumFilters}
            onChange={setForumFilters}
            clearLabel="Clear forums"
          />
        )}
        <MultiSelectDropdown
          emptyLabel="All roles"
          options={ROLE_FILTER_OPTIONS}
          selected={roleFilters}
          onChange={setRoleFilters}
          clearLabel="Clear roles"
        />
        <button
          type="button"
          onClick={() =>
            setRoleFilters((current) =>
              current.includes('advisor')
                ? current.filter((value) => value !== 'advisor')
                : [...current, 'advisor'],
            )
          }
          className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition ${
            roleFilters.includes('advisor')
              ? 'border-navy bg-navy text-white'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Award size={16} />
          Advisor Corps
        </button>
        <button
          type="button"
          onClick={() =>
            setRoleFilters((current) =>
              current.includes('guest')
                ? current.filter((value) => value !== 'guest')
                : [...current, 'guest'],
            )
          }
          className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition ${
            roleFilters.includes('guest')
              ? 'border-navy bg-navy text-white'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Users size={16} />
          Guests
        </button>
        <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap cursor-pointer">
          <input
            type="checkbox"
            checked={pendingOnly}
            onChange={(e) => setPendingOnly(e.target.checked)}
            className="rounded border-gray-300 text-navy focus:ring-gold"
          />
          Invite pending only
        </label>
        {(isAdmin || isFacilitator) && (
          <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300 text-navy focus:ring-gold"
            />
            Deactivated only
          </label>
        )}
      </div>

      {view === 'billing' && (isAdmin || isFacilitator) && (
        <BillingTable
          rows={visibleBillingRows}
          onManage={(id) => setManagingId(id)}
        />
      )}

      {/* Members table */}
      {view === 'members' && (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full stack-table">
            <thead className="bg-off-white">
              <tr>
                <th className="w-10 px-2 py-4" aria-label="Expand" />
                {headers.map(h => (
                  <th
                    key={h.key}
                    onClick={() => toggleSort(h.key)}
                    className="px-6 py-4 text-left text-sm font-semibold text-navy cursor-pointer select-none whitespace-nowrap"
                  >
                    <span className="inline-flex items-center gap-1">
                      {h.label}
                      {sortKey === h.key &&
                        (sortAsc ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={headers.length + 1} className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-navy mx-auto"></div>
                  </td>
                </tr>
              ) : sortedMembers.length > 0 ? (
                sortedMembers.map(member => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    expanded={expandedId === member.id}
                    onToggle={() =>
                      setExpandedId(expandedId === member.id ? null : member.id)
                    }
                    onNavigate={() => navigate(`/members/${member.id}`)}
                    onManage={
                      isAdmin || isFacilitator
                        ? () => setManagingId(member.id)
                        : undefined
                    }
                    facilitatedForumIds={facilitatedForumIds}
                    formatDate={formatDate}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={headers.length + 1} className="px-6 py-12 text-center">
                    <Users className="mx-auto text-gray-300" size={48} />
                    <p className="text-gray-500 mt-4">
                      {search || forumFilters.length || roleFilters.length
                        ? 'No members match your filters'
                        : 'No members found'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {managingMember && (
        <ManageMemberPanel
          member={managingMember}
          forums={forums}
          selfId={user?.id}
          canEditRole={isAdmin}
          canViewBilling={isAdmin || isFacilitator}
          canManageBilling={isAdmin || isFacilitator}
          onClose={() => setManagingId(null)}
          onSaved={() => setReloadKey((k) => k + 1)}
        />
      )}

      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        isAdmin={isAdmin}
        isFacilitator={isFacilitatorRole(user?.role)}
        onInvited={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );
}

type BillingExceptionFilter = 'all' | 'charges' | 'skipped' | 'uncovered';
type DuesBand = 'all' | 'zero' | 'under200' | '200to399' | '400plus';

const DUES_BANDS: { key: DuesBand; label: string }[] = [
  { key: 'all', label: 'Any amount' },
  { key: 'zero', label: '$0' },
  { key: 'under200', label: 'Under $200' },
  { key: '200to399', label: '$200–$399' },
  { key: '400plus', label: '$400+' },
];

function duesInBand(amount: string | null, band: DuesBand): boolean {
  if (band === 'all') return true;
  const n = Number(amount);
  if (!Number.isFinite(n)) return band === 'zero';
  if (band === 'zero') return n === 0;
  if (band === 'under200') return n > 0 && n < 200;
  if (band === '200to399') return n >= 200 && n < 400;
  return n >= 400;
}

const STATUS_FILTERS: { key: PaymentStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'Any status' },
  { key: 'active', label: 'Active' },
  { key: 'trialing', label: 'Signed up' },
  { key: 'past_due', label: 'Past due' },
  { key: 'failed', label: 'Failed' },
  { key: 'pending', label: 'Pending' },
  { key: 'canceled', label: 'Canceled' },
  { key: 'unpaid', label: 'Unpaid' },
];

const rowHasUpcomingCharge = (row: BillingOverviewRow) =>
  row.scheduled_charges.some((c) => c.status === 'scheduled');
const rowIsSkipping = (row: BillingOverviewRow) =>
  Boolean(row.active_skip) || row.is_paused;
const rowHasUncovered = (row: BillingOverviewRow) =>
  row.has_subscription && row.coverage.some((m) => m.uncovered);

// The admin Billing view: a rollup of billing exceptions, then a per-member
// dues table (status, monthly amount, next charge, upcoming activity) with an
// expandable month-by-month timeline. Built from webhook-synced local data —
// no live Stripe calls.
function BillingTable({
  rows,
  onManage,
}: {
  rows: BillingOverviewRow[] | null;
  onManage: (id: number) => void;
}) {
  const [filter, setFilter] = useState<BillingExceptionFilter>('all');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | 'all'>('all');
  const [duesFilter, setDuesFilter] = useState<DuesBand>('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const formatNextCharge = (value: string | null) =>
    value
      ? new Date(value).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : null;

  const formatLastPaid = (row: BillingOverviewRow) => {
    if (!row.last_paid_at) return null;
    const date = formatNextCharge(row.last_paid_at);
    if (row.last_paid_amount) return `$${row.last_paid_amount} · ${date}`;
    return date;
  };

  const counts = useMemo(() => {
    if (!rows) return { charges: 0, skipped: 0, uncovered: 0 };
    return {
      charges: rows.filter(rowHasUpcomingCharge).length,
      skipped: rows.filter(rowIsSkipping).length,
      uncovered: rows.filter(rowHasUncovered).length,
    };
  }, [rows]);

  const visible = useMemo(() => {
    if (!rows) return null;
    return rows.filter((row) => {
      if (filter === 'charges' && !rowHasUpcomingCharge(row)) return false;
      if (filter === 'skipped' && !rowIsSkipping(row)) return false;
      if (filter === 'uncovered' && !rowHasUncovered(row)) return false;
      if (statusFilter !== 'all' && row.payment_status !== statusFilter) return false;
      if (!duesInBand(row.monthly_amount, duesFilter)) return false;
      return true;
    });
  }, [rows, filter, statusFilter, duesFilter]);

  const upcomingSummary = (row: BillingOverviewRow) => {
    const upcoming = row.scheduled_charges.filter((c) => c.status === 'scheduled');
    const failed = row.scheduled_charges.filter((c) => c.status === 'failed');
    const uncoveredMonths = row.has_subscription
      ? row.coverage.filter((m) => m.uncovered).map((m) => m.label)
      : [];
    const lines: React.ReactNode[] = [];
    if (row.active_skip) {
      lines.push(
        <p key="skip" className="text-blue-700">
          Skipping {row.active_skip.months} mo — resumes{' '}
          {formatNextCharge(row.active_skip.resume_on)}
        </p>,
      );
    }
    upcoming.forEach((c) =>
      lines.push(
        <p key={`c${c.id}`} className="text-gray-700">
          ${c.amount} one-time · {formatChargeDate(c.charge_on)}
        </p>,
      ),
    );
    failed.forEach((c) =>
      lines.push(
        <p key={`f${c.id}`} className="text-red-600">
          ${c.amount} failed · {formatChargeDate(c.charge_on)}
        </p>,
      ),
    );
    if (uncoveredMonths.length > 0) {
      lines.push(
        <p key="unc" className="text-amber-600 font-medium">
          ⚠ No dues: {uncoveredMonths.slice(0, 3).join(', ')}
          {uncoveredMonths.length > 3 ? '…' : ''}
        </p>,
      );
    }
    if (lines.length === 0) return <span className="text-gray-400">—</span>;
    return <div className="space-y-0.5">{lines}</div>;
  };

  const rollupChips: { key: BillingExceptionFilter; label: string; count: number; tone: string }[] = [
    { key: 'charges', label: 'upcoming one-time charges', count: counts.charges, tone: 'text-navy' },
    { key: 'skipped', label: 'members skipping / suspended', count: counts.skipped, tone: 'text-blue-700' },
    { key: 'uncovered', label: 'with unbilled months', count: counts.uncovered, tone: 'text-amber-600' },
  ];

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 w-full sm:w-auto">
            Exceptions
          </p>
          {rollupChips.map(({ key, label, count, tone }) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(filter === key ? 'all' : key)}
              className={`px-3 py-1.5 rounded-full text-sm border transition ${
                filter === key
                  ? 'bg-navy text-white border-navy'
                  : 'bg-white border-gray-200 hover:border-navy shadow-sm'
              }`}
            >
              <span className={`font-semibold ${filter === key ? '' : tone}`}>{count}</span>{' '}
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block text-xs font-medium text-gray-600">
            Payment status
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as PaymentStatus | 'all')}
              className="mt-1 block min-w-[10rem] px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
            >
              {STATUS_FILTERS.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-medium text-gray-600">
            Monthly dues
            <select
              value={duesFilter}
              onChange={(e) => setDuesFilter(e.target.value as DuesBand)}
              className="mt-1 block min-w-[10rem] px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
            >
              {DUES_BANDS.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          {(filter !== 'all' || statusFilter !== 'all' || duesFilter !== 'all') && (
            <button
              type="button"
              onClick={() => {
                setFilter('all');
                setStatusFilter('all');
                setDuesFilter('all');
              }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-navy transition"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Next charge says what will actually happen: a date, first charge, $0 · no
        charge, checkout not finished, staff · not billed, or paused. It no
        longer says “No subscription.”
      </p>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden min-w-0">
        <HorizontalScroll>
          <table className="w-full stack-table">
            <thead className="bg-off-white">
              <tr>
                <th className="w-8 px-1.5 py-3" aria-label="Expand" />
                {['Member', 'Status', 'Dues', 'Last paid', 'Card', 'Next charge', 'One-time'].map(
                  (label, i) => (
                    <th
                      key={i}
                      className="px-3 py-3 text-left text-sm font-semibold text-navy whitespace-nowrap"
                    >
                      {label}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible === null ? (
                <tr>
                    <td colSpan={8} className="px-3 py-12 text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-navy mx-auto"></div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-12 text-center">
                    <Users className="mx-auto text-gray-300" size={48} />
                    <p className="text-gray-500 mt-4">
                      {filter !== 'all' || statusFilter !== 'all' || duesFilter !== 'all'
                        ? 'No members match these billing filters'
                        : 'No dues-paying members match your filters'}
                    </p>
                  </td>
                </tr>
              ) : (
                visible.map((row) => {
                  const nextCharge = earliestUpcomingCharge(row);
                  return (
                  <React.Fragment key={row.id}>
                    <tr className="hover:bg-off-white/50">
                      <td data-label="Timeline" className="px-1.5 py-3 text-center">
                        {row.has_subscription && row.coverage.length > 0 && (
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedId(expandedId === row.id ? null : row.id)
                            }
                            aria-label="Show billing timeline"
                            className="p-1 text-gray-400 hover:text-navy transition"
                          >
                            {expandedId === row.id ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <p className="font-medium text-navy truncate">{row.full_name}</p>
                        <p className="text-sm text-gray-500 truncate">{row.email}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          <button
                            type="button"
                            onClick={() => onManage(row.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
                          >
                            <SlidersHorizontal size={12} />
                            Manage
                          </button>
                        </div>
                      </td>
                      <td data-label="Status" className="px-3 py-3 whitespace-nowrap">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            PAYMENT_BADGES[row.payment_status] ?? 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {capitalize(paymentLabel(row.payment_status))}
                        </span>
                        {row.is_paused && (
                          <span className="ml-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                            Paused
                          </span>
                        )}
                        {row.cancel_at_period_end && row.payment_status !== 'canceled' && (
                          <span
                            className="ml-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-800 text-white"
                            title="Canceled in Stripe. They keep access until the period ends."
                          >
                            Leaving
                          </span>
                        )}
                        {row.limit_app_access && (
                          <span className="ml-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            Limited access
                          </span>
                        )}
                        {isZeroDues(row.monthly_amount) && (
                          <span className="ml-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-navy/10 text-navy">
                            $0 · no charge
                          </span>
                        )}
                        {STAFF_ROLES.has(row.role) && (
                          <span className="ml-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            Staff
                          </span>
                        )}
                      </td>
                      <td data-label="Dues" className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {row.monthly_amount != null ? `$${row.monthly_amount}/mo` : '—'}
                      </td>
                      <td data-label="Last paid" className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {formatLastPaid(row) ?? '—'}
                      </td>
                      <td data-label="Card" className="px-3 py-3 text-sm whitespace-nowrap">
                        <span className={row.has_card ? 'text-navy font-medium' : 'text-gray-400'}>
                          {row.has_card ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td data-label="Next charge" className="px-3 py-3 text-sm whitespace-nowrap">
                        {(() => {
                          const caption = nextChargeCaption(row, nextCharge);
                          return <span className={caption.className}>{caption.text}</span>;
                        })()}
                      </td>
                      <td data-label="One-time" className="px-3 py-3 text-sm">{upcomingSummary(row)}</td>
                    </tr>
                    {expandedId === row.id && (
                      <tr className="bg-off-white/40">
                        <td />
                        <td colSpan={7} className="px-3 py-3">
                          <div className="max-w-lg">
                            <CoverageTimeline
                              months={row.coverage}
                              pauseReason={row.pause_reason}
                              skipReason={row.active_skip?.reason}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </HorizontalScroll>
      </div>
    </div>
  );
}

function MemberRow({
  member,
  expanded,
  onToggle,
  onNavigate,
  onManage,
  facilitatedForumIds,
  formatDate,
}: {
  member: AdminMemberRow;
  expanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
  onManage?: () => void;
  facilitatedForumIds: Set<number>;
  formatDate: (dateString: string) => string;
}) {
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [inviteSent, setInviteSent] = useState({
    at: member.invite_sent_at,
    by: member.invite_sent_by_name,
  });
  const forumId = member.forums.find((forum) => facilitatedForumIds.has(forum.id))?.id;
  const inviteLabel = member.pending_setup ? inviteSentLabel(inviteSent.at, inviteSent.by) : null;

  const handleResend = async () => {
    if (!confirmResendInvite(member.full_name, inviteSent.at, inviteSent.by)) return;
    setResending(true);
    try {
      const result = await usersAPI.resendInvite(member.id);
      setInviteSent({
        at: result.invite_sent_at ?? new Date().toISOString(),
        by: result.invite_sent_by_name ?? null,
      });
      toast.success(result.message || 'Setup link re-sent');
    } catch {
      toast.error("Couldn't resend the invite");
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <tr
        onClick={onToggle}
        className="hover:bg-off-white/50 transition cursor-pointer"
      >
        <td className="stack-hide px-2 py-4 text-center">
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            aria-label={expanded ? 'Hide details' : 'Show details'}
            aria-expanded={expanded}
            className="p-1.5 rounded-lg text-gray-400 hover:text-navy hover:bg-off-white transition"
          >
            <ChevronRight
              size={16}
              className={`transition-transform ${expanded ? 'rotate-90' : ''}`}
            />
          </button>
        </td>
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            {member.avatar_url ? (
              <img
                src={member.avatar_url}
                alt=""
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center text-white font-bold">
                {member.first_name?.[0] || member.username[0]}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <p className="font-medium text-navy truncate">{member.full_name}</p>
                {!member.is_active && (
                  <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">
                    {member.deletion_requested_at ? 'Deletion requested' : 'Deactivated'}
                  </span>
                )}
                {member.pending_setup && (
                  <span
                    className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700"
                    title="Invited, but hasn't set up their account yet"
                  >
                    Invite pending
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 truncate">{member.email}</p>
              {inviteLabel && <p className="text-xs text-gray-500">{inviteLabel}</p>}
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                <button
                  onClick={(e) => { e.stopPropagation(); onNavigate(); }}
                  className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  <ExternalLink size={12} />
                  Profile
                </button>
                {forumId != null && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/forum/${forumId}/member/${member.id}`);
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
                    title="Snapshots, grow goals, prayers, and your notes"
                  >
                    <ClipboardList size={12} />
                    Details
                  </button>
                )}
                {onManage && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onManage(); }}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
                  >
                    <SlidersHorizontal size={12} />
                    Manage
                  </button>
                )}
                {member.pending_setup && member.is_active && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleResend(); }}
                    disabled={resending}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-amber-200 rounded-lg text-xs font-medium text-amber-700 hover:bg-amber-50 transition disabled:opacity-50"
                  >
                    {resending ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Send size={12} />
                    )}
                    Resend
                  </button>
                )}
              </div>
            </div>
          </div>
        </td>
        <td data-label="Forum" className="px-6 py-4 text-sm text-gray-700">
          {member.forums.length > 0 ? (
            member.forums.map(f => f.name).join(', ')
          ) : (
            <span className="text-xs text-gray-400 italic">Unassigned</span>
          )}
        </td>
        <td data-label="Role" className="px-6 py-4 text-sm text-gray-700">{roleLabel(member.role)}</td>
        <td data-label="City / State" className="px-6 py-4 text-sm text-gray-700">
          {[member.home_city || member.location_city, member.home_state || member.location_state]
            .filter(Boolean)
            .join(', ') || <span className="text-xs text-gray-400">—</span>}
        </td>
        <td data-label="Payment" className="px-6 py-4">
          <span
            className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${
              PAYMENT_BADGES[member.payment_status] ?? 'bg-gray-100 text-gray-600'
            }`}
          >
            {paymentLabel(member.payment_status)}
          </span>
          {member.is_paused && (
            <span className="ml-1.5 inline-flex px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              Paused
            </span>
          )}
          {member.limit_app_access && (
            <span className="ml-1.5 inline-flex px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Limited access
            </span>
          )}
        </td>
        <td data-label="Joined" className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
          {formatDate(member.date_joined)}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="p-0">
            <MemberDetailPanel member={member} />
          </td>
        </tr>
      )}
    </>
  );
}
